"use client"

import { Link } from "react-router-dom"
import { useRef, useEffect, useState } from "react"
import { useTheme } from "../contexts/ThemeContext"

function useScrollAnimation() {
  useEffect(() => {
    const elements = document.querySelectorAll('.animate-on-scroll');
    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-scrollVisible');
          entry.target.classList.remove('animate-scrollHidden');
        } else {
          entry.target.classList.remove('animate-scrollVisible');
          entry.target.classList.add('animate-scrollHidden');
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -100px 0px' });
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function Home() {
  const examples = [
    {
      id: 1,
      type: "text-to-image",
      prompt: "A majestic dragon soaring through a mystical forest at sunset, with glowing eyes and scales that shimmer like precious gems",
      image: "https://replicate.delivery/xezq/EFHBWTlmkzJ6GpwjvMeAJC1JLDsWV0ZTMPHfrmdzvEGyeS3pA/out-0.png",
      category: "Text to Image"
    },
    {
      id: 2,
      type: "transform-image",
      prompt: "Portrait transformed into digital art style",
      image: "https://replicate.delivery/xezq/MueAUlcYUdRbayTpxu0ncpbfV9WLbRe9hr5C8y9gl3uoCT3pA/out-0.png",
      category: "Transform Image"
    },
    {
      id: 3,
      type: "image-to-video",
      prompt: "Car is moving fast and camera angle is rotated to shoot from front of car",
      image: "https://replicate.delivery/pbxt/Nlmkw2PNgplCWp3pRw5xAm7ahWFtzjkW6EvUfVSUkKxSCJ0H/ai-generated-image-1758653270546.jpg",
      videoUrl: "https://replicate.delivery/xezq/VTr2Rn0fRARBAKat2Ua501qNQmVmKxG1Zk8TBm6otk6i3OsKA/output_30fps.mp4",
      category: "Image to Video",
      isVideo: true
    },
    {
      id: 4,
      type: "text-to-image",
      prompt: "A steampunk airship floating above Victorian-era London, with brass gears, steam pipes, and ornate details",
      image: "https://replicate.delivery/xezq/YzuJsGV2efn7L0T1nBUsnJobZ1kRpa3UEoBGeWSchSQfJmuTB/out-0.png",
      category: "Text to Image"
    },
    {
      id: 5,
      type: "transform-image",
      prompt: "Photograph converted to anime art style",
      image: "https://replicate.delivery/xezq/0TpV6Sr2CHLFKR9W4ck5v2oS2c7EK2eOMEHdzQDM40tgx0dKA/out-0.png",
      category: "Transform Image"
    },
    {
      id: 6,
      type: "image-to-video",
      prompt: "Character portrait with subtle facial animation and breathing",
      image: "https://replicate.delivery/xezq/9eOh9RH6AlRAZC4TuP513zXxvAWRZnjp5TqWJGEgIfIljp7UA/out-0.png",
      videoUrl: "https://replicate.delivery/xezq/VTr2Rn0fRARBAKat2Ua501qNQmVmKxG1Zk8TBm6otk6i3OsKA/output_30fps.mp4",
      category: "Image to Video",
      isVideo: true
    }
  ]

  const features = [
    {
      icon: "🚀",
      title: "Lightning Fast",
      description: "Generate high-quality images in seconds with our optimized AI model"
    },
    {
      icon: "🧠",
      title: "Unlimited Creativity",
      description: "Create any image you can imagine with simple text prompts"
    },
    {
      icon: "🌟",
      title: "Premium Quality",
      description: "Get stunning, high-resolution images perfect for any project"
    },
    {
      icon: "🛡️",
      title: "Secure & Private",
      description: "Your prompts and generated images are kept private and secure"
    },
    {
      icon: "🤩",
      title: "Easy to Use",
      description: "Simple interface that anyone can use, no technical skills required"
    },
    {
      icon: "🪙",
      title: "Flexible Pricing",
      description: "Pay only for what you use with our credit-based system"
    }
  ]

  const promptExamples = [
    "A cyberpunk street scene with neon lights and rain",
    "A magical forest with glowing mushrooms and fairy lights",
    "A steampunk robot with brass gears and steam vents",
    "A futuristic space station orbiting a colorful nebula",
    "A cozy coffee shop with warm lighting and bookshelves",
    "A majestic castle on a floating island in the sky"
  ]

  const pricingRef = useRef(null)
  const galleryRef = useRef(null)
  const [isPaused, setIsPaused] = useState(false)
  const { theme } = useTheme();

  // Smooth scroll handler
  const handleScrollToPricing = (e) => {
    e.preventDefault()
    if (pricingRef.current) {
      pricingRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  // Professional gallery images for horizontal scrollable canvas
  const galleryImages = [
    "https://replicate.delivery/xezq/CBlIb3LGM86DHdNuGMneW7bT3qIgfIV2jioUJZSu1JI7YHAVA/out-0.png",
    "https://replicate.delivery/xezq/vkHoNIgCdOL9ExA0eOfTwjcVb9xARdukeIH9SnQfK5ShgdAUB/out-0.png",
    "https://replicate.delivery/xezq/4oTFukexU10AaycbCOejiIaByNQz2OmpkcgUsdjuZdiwWHAVA/out-0.png",
    "https://replicate.delivery/xezq/1qDXNYxfQ6wEOCYC90fxEPp7exFIQ0C46lk90FfifSdHy6AoC/out-0.png",
    "https://replicate.delivery/xezq/dhzicfPpk4V1QqHgbvQf7jC4DDeeWqHeJXBixJx51d5Xs6AoC/out-0.png",
    "https://replicate.delivery/xezq/cTFwEoysL9rwKNyueRRmYMBEYnwfumRrhZiBvqADbIymQHAVA/out-0.png",
  ];

  // Shuffle galleryImages for random order
  const shuffledGalleryImages = [...galleryImages].sort(() => Math.random() - 0.5);

  // Duplicate images for infinite scroll
  const duplicatedImages = [...shuffledGalleryImages, ...shuffledGalleryImages];

  // Auto-scroll functionality
  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    const scrollWidth = gallery.scrollWidth / 2; // Half because we duplicated images
    const clientWidth = gallery.clientWidth;
    let scrollPosition = 0;

    const autoScroll = () => {
      if (isPaused) return;

      scrollPosition += 1; // Smooth scroll speed
      gallery.scrollLeft = scrollPosition;

      // Reset to beginning when reaching the end of first set
      if (scrollPosition >= scrollWidth) {
        scrollPosition = 0;
        gallery.scrollLeft = 0;
      }
    };

    const interval = setInterval(autoScroll, 30); // 30ms for smooth animation

    return () => clearInterval(interval);
  }, [isPaused, shuffledGalleryImages]);

  useScrollAnimation();

  useEffect(() => {
    // Animate logo rotation
    const logo = document.querySelector('.logo-animate');
    if (logo) {
      logo.classList.add('animate-logoSpin');
    }
  }, []);

  return (
    <div className={theme === "dark" ? "min-h-screen bg-black text-white" : "min-h-screen bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100"}>
      {/* Hero Section - Clean Modern Centered */}
      <section className={theme === "dark" ? "relative min-h-[80vh] flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800 overflow-hidden animate-on-scroll animate-heavyFadeInUp" : "relative min-h-[80vh] flex items-center justify-center bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 overflow-hidden animate-on-scroll animate-heavyFadeInUp"}>
        {/* Glassy floating shapes */}
        <div className={theme === "dark" ? "absolute -top-32 -left-32 w-96 h-96 bg-gray-700 opacity-20 rounded-full blur-3xl animate-pulse animate-heavyFloat z-0" : "absolute -top-32 -left-32 w-96 h-96 bg-pink-400 opacity-30 rounded-full blur-3xl animate-pulse animate-heavyFloat z-0"} />
        <div className={theme === "dark" ? "absolute -bottom-32 -right-32 w-96 h-96 bg-gray-600 opacity-20 rounded-full blur-3xl animate-pulse animate-heavyFloatReverse z-0" : "absolute -bottom-32 -right-32 w-96 h-96 bg-indigo-400 opacity-30 rounded-full blur-3xl animate-pulse animate-heavyFloatReverse z-0"} />
        <div className={theme === "dark" ? "absolute top-1/3 left-1/2 w-80 h-80 bg-gray-800/20 rounded-3xl blur-2xl -translate-x-1/2 -translate-y-1/2 z-0 animate-heavyScale" : "absolute top-1/3 left-1/2 w-80 h-80 bg-white/10 rounded-3xl blur-2xl -translate-x-1/2 -translate-y-1/2 z-0 animate-heavyScale"} />
        <div className={theme === "dark" ? "absolute top-10 right-1/4 w-40 h-40 bg-gradient-to-br from-gray-500 via-gray-400 to-gray-600 opacity-15 rounded-full blur-2xl animate-float animate-heavyRotate z-0" : "absolute top-10 right-1/4 w-40 h-40 bg-gradient-to-br from-yellow-300 via-pink-300 to-indigo-300 opacity-20 rounded-full blur-2xl animate-float animate-heavyRotate z-0"} />
        <div className={theme === "dark" ? "absolute bottom-10 left-1/4 w-32 h-32 bg-gradient-to-tr from-gray-600 via-gray-500 to-gray-700 opacity-15 rounded-full blur-2xl animate-float-slow animate-heavyScale z-0" : "absolute bottom-10 left-1/4 w-32 h-32 bg-gradient-to-tr from-indigo-400 via-purple-300 to-pink-300 opacity-25 rounded-full blur-2xl animate-float-slow animate-heavyScale z-0"} />
        <div className={theme === "dark" ? "absolute top-1/2 right-10 w-24 h-24 bg-gray-700/30 rounded-2xl blur-xl animate-float-tiny animate-heavyRotate z-0" : "absolute top-1/2 right-10 w-24 h-24 bg-white/20 rounded-2xl blur-xl animate-float-tiny animate-heavyRotate z-0"} />
        <div className={theme === "dark" ? "absolute bottom-1/3 left-10 w-28 h-28 bg-gray-600/20 rounded-full blur-2xl animate-float-tiny animate-heavyScale z-0" : "absolute bottom-1/3 left-10 w-28 h-28 bg-pink-200/30 rounded-full blur-2xl animate-float-tiny animate-heavyScale z-0"} />
        <style>{`
          @keyframes float { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-20px) scale(1.05); } }
          @keyframes float-slow { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(30px) scale(1.08); } }
          @keyframes float-tiny { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-10px) scale(1.03); } }
          @keyframes heavyFloat { 0%,100%{transform:translateY(0) scale(1);} 25%{transform:translateY(-40px) scale(1.1);} 50%{transform:translateY(40px) scale(1.15);} 75%{transform:translateY(-20px) scale(1.05);} }
          @keyframes heavyFloatReverse { 0%,100%{transform:translateY(0) scale(1);} 25%{transform:translateY(40px) scale(1.1);} 50%{transform:translateY(-40px) scale(1.15);} 75%{transform:translateY(20px) scale(1.05);} }
          @keyframes heavyScale { 0%,100%{transform:scale(1);} 50%{transform:scale(1.2);} }
          @keyframes heavyRotate { 0%{transform:rotate(0deg);} 100%{transform:rotate(360deg);} }
          @keyframes logoSpin { 0%{transform:rotate(0deg);} 100%{transform:rotate(720deg);} }
          @keyframes heavyFadeInUp { from { opacity: 0; transform: translateY(80px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
          @keyframes popIn { 0%{transform:scale(0.7); opacity:0;} 80%{transform:scale(1.1); opacity:1;} 100%{transform:scale(1); opacity:1;} }
          @keyframes bounceIn { 0%{transform:scale(0.8); opacity:0;} 60%{transform:scale(1.15); opacity:1;} 80%{transform:scale(0.95);} 100%{transform:scale(1);} }
          @keyframes galleryEntrance { 0%{transform:translateY(60px) scale(0.9) rotate(-8deg); opacity:0;} 80%{transform:translateY(-10px) scale(1.05) rotate(8deg); opacity:1;} 100%{transform:translateY(0) scale(1) rotate(0deg); opacity:1;} }
          .animate-heavyFloat { animation: heavyFloat 4s ease-in-out infinite; }
          .animate-heavyFloatReverse { animation: heavyFloatReverse 4s ease-in-out infinite; }
          .animate-heavyScale { animation: heavyScale 3s ease-in-out infinite; }
          .animate-heavyRotate { animation: heavyRotate 6s linear infinite; }
          .animate-logoSpin { animation: logoSpin 1.5s cubic-bezier(.4,0,.2,1) both; }
          .animate-heavyFadeInUp { animation: heavyFadeInUp 0.8s cubic-bezier(.4,0,.2,1) both; }
          .animate-popIn { animation: popIn 0.6s cubic-bezier(.4,0,.2,1) both; }
          .animate-bounceIn { animation: bounceIn 0.7s cubic-bezier(.4,0,.2,1) both; }
          .animate-galleryEntrance { animation: galleryEntrance 0.8s cubic-bezier(.4,0,.2,1) both; }
        `}</style>
        <div className={theme === "dark" ? "relative z-10 w-full max-w-3xl mx-auto px-4 py-24 flex flex-col items-center text-center backdrop-blur-2xl bg-gray-900/50 border border-gray-700 rounded-3xl shadow-2xl animate-popIn" : "relative z-10 w-full max-w-3xl mx-auto px-4 py-24 flex flex-col items-center text-center backdrop-blur-2xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl animate-popIn"}>
          <div className="flex justify-center mb-8">
            <div className={theme === "dark" ? "w-20 h-20 bg-gray-800/50 rounded-3xl flex items-center justify-center shadow-xl border border-gray-700 logo-animate" : "w-20 h-20 bg-white/30 rounded-3xl flex items-center justify-center shadow-xl border border-white/40 logo-animate"}>
              <img src="/brand-logo.svg" alt="Logo" className="h-16 w-16" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6 drop-shadow-xl animate-fadeInUp animate-bounceIn">
            <span className={theme === "dark" ? "block text-transparent bg-clip-text bg-gradient-to-r from-gray-100 via-gray-300 to-gray-500 animate-gradientText" : "block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-pink-400 to-indigo-400 animate-gradientText"}>
              Create, Transform & Animate with AI
            </span>
          </h1>
          <p className={theme === "dark" ? "text-xl md:text-2xl text-gray-300 mb-10 max-w-2xl mx-auto drop-shadow animate-fadeInUp delay-200 animate-popIn" : "text-xl md:text-2xl text-gray-200 mb-10 max-w-2xl mx-auto drop-shadow animate-fadeInUp delay-200 animate-popIn"}>
            Generate stunning images from text, transform existing images, and create captivating videos - all powered by cutting-edge AI technology.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center animate-fadeInUp delay-300 animate-popIn">
            <Link
              to="/register"
              className={theme === "dark" ? "px-12 py-5 rounded-2xl font-extrabold text-xl bg-gray-800 text-white shadow-2xl hover:shadow-[0_0_32px_0_rgba(75,85,99,0.4)] transition-all duration-200 border-2 border-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-600 animate-glow animate-bounceIn" : "px-12 py-5 rounded-2xl font-extrabold text-xl bg-gradient-to-r from-yellow-400 via-pink-500 to-indigo-500 text-white shadow-2xl hover:shadow-[0_0_32px_0_rgba(236,72,153,0.4)] transition-all duration-200 border-2 border-white/30 focus:outline-none focus:ring-4 focus:ring-pink-300 animate-glow animate-bounceIn"}
              style={{ letterSpacing: '0.03em' }}
            >
              Start Creating Free
            </Link>
            <Link
              to="/login"
              className={theme === "dark" ? "px-12 py-5 rounded-2xl font-extrabold text-xl border-2 border-gray-600 text-gray-200 bg-black/30 hover:bg-gray-800/50 shadow-xl hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-gray-600 animate-bounceIn" : "px-12 py-5 rounded-2xl font-extrabold text-xl border-2 border-white/80 text-white bg-white/10 hover:bg-white/20 shadow-xl hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-indigo-300 animate-bounceIn"}
              style={{ letterSpacing: '0.03em' }}
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Image to Video Transformation Showcase */}
      <section className={theme === "dark" ? "py-20 bg-black relative overflow-hidden" : "py-20 bg-gradient-to-br from-purple-100 via-pink-50 to-red-50 relative overflow-hidden"}>
        <div className={theme === "dark" ? "absolute inset-0 bg-gradient-to-r from-gray-800/10 via-gray-700/10 to-gray-600/10" : "absolute inset-0 bg-gradient-to-r from-red-500/10 via-pink-500/10 to-purple-500/10"}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-on-scroll animate-scrollHidden">
            <h2 className={theme === "dark" ? "text-4xl font-extrabold text-gray-200 mb-4 drop-shadow" : "text-4xl font-extrabold text-gray-900 mb-4 drop-shadow"}>See Images Come to Life</h2>
            <p className={theme === "dark" ? "text-xl text-gray-300 max-w-3xl mx-auto" : "text-xl text-gray-600 max-w-3xl mx-auto"}>
              Watch how our AI transforms static images into dynamic, animated videos with just a text prompt
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch relative">
              {/* Before - Static Image */}
              <div className={theme === "dark" ? "group perspective-1000 animate-on-scroll animate-scrollHidden" : "group perspective-1000 animate-on-scroll animate-scrollHidden"}>
                <div className="relative transform-style-3d transition-transform duration-700 group-hover:rotateY-8 group-hover:scale-105 h-full">
                  <div className={theme === "dark" ? "bg-gradient-to-br from-gray-800 via-gray-900 to-black border-2 border-gray-600 rounded-3xl p-6 shadow-2xl backdrop-blur-xl relative z-10 h-full flex flex-col" : "bg-gradient-to-br from-white via-purple-50 to-pink-50 border-2 border-purple-200 rounded-3xl p-6 shadow-2xl backdrop-blur-xl relative z-10 h-full flex flex-col"}>
                    <div className="text-center mb-4">
                      <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-200 mb-2" : "text-2xl font-bold text-gray-900 mb-2"}>Static Image</h3>
                      <span className={theme === "dark" ? "bg-gray-700 text-gray-300 px-4 py-1 rounded-full text-sm" : "bg-purple-100 text-purple-700 px-4 py-1 rounded-full text-sm"}>Original</span>
                    </div>
                    <div className="relative rounded-2xl overflow-hidden flex-1">
                      <img
                        src="https://replicate.delivery/pbxt/Nlmkw2PNgplCWp3pRw5xAm7ahWFtzjkW6EvUfVSUkKxSCJ0H/ai-generated-image-1758653270546.jpg"
                        alt="Static car image"
                        className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/placeholder.jpg";
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                    <div className={theme === "dark" ? "mt-4 p-4 bg-gray-800/50 rounded-xl border border-gray-600" : "mt-4 p-4 bg-white/50 rounded-xl border border-purple-200"}>
                      <p className={theme === "dark" ? "text-gray-300 text-sm italic" : "text-gray-700 text-sm italic"}>
                        "Car is moving fast and camera angle is rotated to shoot from front of car"
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Magic Arrow - Positioned between cards */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 hidden lg:flex">
                <div className={theme === "dark" ? "flex items-center space-x-4 bg-gradient-to-r from-red-500 to-pink-500 px-6 py-3 rounded-full shadow-lg animate-pulse" : "flex items-center space-x-4 bg-gradient-to-r from-red-500 to-pink-500 px-6 py-3 rounded-full shadow-lg animate-pulse"}>
                  <span className="text-white font-bold text-sm">AI Magic</span>
                  <svg className="w-6 h-6 text-white animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>

              {/* After - Animated Video */}
              <div className={theme === "dark" ? "group perspective-1000 animate-on-scroll animate-scrollHidden" : "group perspective-1000 animate-on-scroll animate-scrollHidden"} style={{ animationDelay: '0.2s' }}>
                <div className="relative transform-style-3d transition-transform duration-700 group-hover:rotateY-8 group-hover:scale-105 h-full">
                  <div className={theme === "dark" ? "bg-gradient-to-br from-gray-800 via-gray-900 to-black border-2 border-red-500 rounded-3xl p-6 shadow-2xl backdrop-blur-xl relative z-10 hover:shadow-[0_25px_50px_-12px_rgba(239,68,68,0.25)] h-full flex flex-col" : "bg-gradient-to-br from-white via-red-50 to-pink-50 border-2 border-red-300 rounded-3xl p-6 shadow-2xl backdrop-blur-xl relative z-10 hover:shadow-[0_25px_50px_-12px_rgba(239,68,68,0.25)] h-full flex flex-col"}>
                    <div className="absolute inset-0 bg-gradient-to-r from-red-400/10 via-pink-500/10 to-purple-500/10 rounded-3xl"></div>
                    <div className="text-center mb-4 relative z-10">
                      <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-200 mb-2" : "text-2xl font-bold text-gray-900 mb-2"}>Animated Video</h3>
                      <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-medium shadow">
                        AI Generated
                      </span>
                    </div>
                    <div className="relative rounded-2xl overflow-hidden flex-1">
                      <video
                        className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700"
                        autoPlay
                        loop
                        muted
                        playsInline
                      >
                        <source src="https://replicate.delivery/xezq/VTr2Rn0fRARBAKat2Ua501qNQmVmKxG1Zk8TBm6otk6i3OsKA/output_30fps.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                      <div className="absolute top-4 right-4">
                        <div className="bg-black/80 text-white p-2 rounded-full shadow-lg animate-pulse">
                          <span className="text-lg">🎬</span>
                        </div>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="bg-black/60 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm">
                          ✨ Watch the AI bring this image to life!
                        </div>
                      </div>
                    </div>
                    <div className={theme === "dark" ? "mt-4 p-4 bg-gradient-to-r from-red-900/30 to-pink-900/30 rounded-xl border border-red-500/50" : "mt-4 p-4 bg-gradient-to-r from-red-100 to-pink-100 rounded-xl border border-red-300"}>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-red-500 text-sm font-semibold">🚗 Motion Added:</span>
                      </div>
                      <ul className={theme === "dark" ? "text-gray-300 text-sm space-y-1" : "text-gray-700 text-sm space-y-1"}>
                        <li>• Dynamic camera movement</li>
                        <li>• Smooth motion effects</li>
                        <li>• Realistic perspective shifts</li>
                        <li>• 30fps high-quality animation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mobile AI Magic indicator - shown below on smaller screens */}
            <div className="flex justify-center mt-8 lg:hidden">
              <div className={theme === "dark" ? "flex items-center space-x-4 bg-gradient-to-r from-red-500 to-pink-500 px-8 py-4 rounded-full shadow-lg animate-pulse" : "flex items-center space-x-4 bg-gradient-to-r from-red-500 to-pink-500 px-8 py-4 rounded-full shadow-lg animate-pulse"}>
                <span className="text-white font-bold text-lg">AI Magic</span>
                <svg className="w-8 h-8 text-white animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-16">
            <Link
              to="/register"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-red-500 via-pink-500 to-purple-500 text-white px-12 py-6 rounded-2xl font-extrabold text-xl shadow-2xl hover:shadow-[0_0_40px_0_rgba(239,68,68,0.4)] transition-all duration-300 transform hover:scale-105 animate-bounce"
            >
              <span>🎬</span>
              <span>Try Image to Video Now</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Amazing Gallery Section - Horizontal Scrollable Canvas */}
      <section className={theme === "dark" ? "py-20 bg-gray-900 animate-on-scroll animate-scrollHidden" : "py-20 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 animate-on-scroll animate-scrollHidden"}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll animate-scrollHidden">
            <h2 className={theme === "dark" ? "text-4xl font-extrabold text-gray-200 mb-4 drop-shadow animate-gradientText" : "text-4xl font-extrabold text-indigo-700 mb-4 drop-shadow animate-gradientText"}>Amazing Gallery Generated by Our Tool</h2>
            <p className={theme === "dark" ? "text-xl text-gray-300 max-w-3xl mx-auto" : "text-xl text-gray-600 max-w-3xl mx-auto"}>Scroll through a stunning gallery of professional images created by our users and AI model. Every image is a testament to creativity and technology!</p>
          </div>
          <div 
            ref={galleryRef}
            className="overflow-x-auto overflow-y-hidden pb-4 scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="flex space-x-8 min-w-max">
              {duplicatedImages.map((imgUrl, idx) => (
                <div key={idx} className={theme === "dark" ? "flex-shrink-0 w-72 h-80 bg-gray-800 rounded-3xl shadow-2xl border-4 border-gray-700 overflow-hidden hover:scale-110 hover:rotate-3 hover:shadow-[0_0_32px_0_rgba(75,85,99,0.3)] transition-transform duration-300 group relative animate-on-scroll animate-scrollHidden" : "flex-shrink-0 w-72 h-80 bg-gradient-to-br from-indigo-200 via-purple-200 to-pink-200 rounded-3xl shadow-2xl border-4 border-white/60 overflow-hidden hover:scale-110 hover:rotate-3 hover:shadow-[0_0_32px_0_rgba(236,72,153,0.3)] transition-transform duration-300 group relative animate-on-scroll animate-scrollHidden"} style={{ animationDelay: `${idx * 0.1}s` }}>
                  <img
                    src={imgUrl}
                    alt="AI generated artwork"
                    className="w-full h-full object-cover object-center rounded-2xl group-hover:brightness-110 group-hover:contrast-125 transition-all duration-300 animate-zoomIn"
                    style={{ aspectRatio: '4/5' }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/placeholder.jpg";
                    }}
                    loading="lazy"
                  />
                  <div className={theme === "dark" ? "absolute inset-0 pointer-events-none group-hover:bg-gray-800/10 transition-all duration-300 rounded-2xl" : "absolute inset-0 pointer-events-none group-hover:bg-white/10 transition-all duration-300 rounded-2xl"} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By / Testimonials Section
      <section className={theme === "dark" ? "py-16 bg-black border-b border-gray-800 animate-on-scroll animate-heavyFadeInUp" : "py-16 bg-white/80 backdrop-blur-md border-b border-indigo-100 animate-on-scroll animate-heavyFadeInUp"}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className={theme === "dark" ? "text-2xl font-bold text-gray-200 mb-2" : "text-2xl font-bold text-gray-900 mb-2"}>Trusted by Creators Worldwide</h2>
            <p className={theme === "dark" ? "text-gray-400" : "text-gray-600"}>Join thousands of artists, designers, and innovators using Inflatein AI Studio</p>
          </div>
          <div className="text-center mb-8">
            <span className={theme === "dark" ? "text-2xl text-gray-300 font-bold" : "text-2xl text-gray-400 font-bold"}>+ 5,000 Happy Users</span>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className={theme === "dark" ? "bg-black border border-gray-700 rounded-xl shadow p-6" : "bg-white rounded-xl shadow p-6 border border-indigo-100"}>
              <p className={theme === "dark" ? "text-gray-300 italic mb-4" : "text-gray-700 italic mb-4"}>"Absolutely mind-blowing results! The images are stunning and the process is so easy."</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                  PS
                </div>
                <div>
                  <div className={theme === "dark" ? "font-semibold text-gray-200" : "font-semibold text-gray-900"}>Priya S.</div>
                  <div className={theme === "dark" ? "text-xs text-gray-400" : "text-xs text-gray-500"}>Digital Artist</div>
                </div>
              </div>
            </div>
            <div className={theme === "dark" ? "bg-black border border-gray-700 rounded-xl shadow p-6" : "bg-white rounded-xl shadow p-6 border border-indigo-100"}>
              <p className={theme === "dark" ? "text-gray-300 italic mb-4" : "text-gray-700 italic mb-4"}>"I use it for all my design projects now. Fast, reliable, and the quality is unmatched."</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
                  AR
                </div>
                <div>
                  <div className={theme === "dark" ? "font-semibold text-gray-200" : "font-semibold text-gray-900"}>Alex R.</div>
                  <div className={theme === "dark" ? "text-xs text-gray-400" : "text-xs text-gray-500"}>UI Designer</div>
                </div>
              </div>
            </div>
            <div className={theme === "dark" ? "bg-black border border-gray-700 rounded-xl shadow p-6" : "bg-white rounded-xl shadow p-6 border border-indigo-100"}>
              <p className={theme === "dark" ? "text-gray-300 italic mb-4" : "text-gray-700 italic mb-4"}>"The best AI image tool I've tried. The results are always impressive!"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                  MT
                </div>
                <div>
                  <div className={theme === "dark" ? "font-semibold text-gray-200" : "font-semibold text-gray-900"}>Meera T.</div>
                  <div className={theme === "dark" ? "text-xs text-gray-400" : "text-xs text-gray-500"}>Content Creator</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Features Section */}
      <section className={theme === "dark" ? "py-20 bg-black relative overflow-hidden animate-on-scroll animate-scrollHidden" : "py-20 bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 relative overflow-hidden animate-on-scroll animate-scrollHidden"}>
        {/* Decorative blurred shapes */}
        <div className={theme === "dark" ? "absolute -top-32 -left-32 w-96 h-96 bg-gray-700 opacity-10 rounded-full blur-3xl animate-pulse z-0" : "absolute -top-32 -left-32 w-96 h-96 bg-pink-300 opacity-20 rounded-full blur-3xl animate-pulse z-0"} />
        <div className={theme === "dark" ? "absolute -bottom-32 -right-32 w-96 h-96 bg-gray-600 opacity-10 rounded-full blur-3xl animate-pulse z-0" : "absolute -bottom-32 -right-32 w-96 h-96 bg-indigo-300 opacity-20 rounded-full blur-3xl animate-pulse z-0"} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-on-scroll animate-scrollHidden">
            <h2 className={theme === "dark" ? "text-4xl font-extrabold text-gray-200 mb-2 drop-shadow" : "text-4xl font-extrabold text-gray-900 mb-2 drop-shadow"}>Why Choose Inflatein AI Studio?</h2>
            <p className={theme === "dark" ? "text-lg text-gray-400 font-semibold tracking-wide uppercase mb-2" : "text-lg text-indigo-500 font-semibold tracking-wide uppercase mb-2"}>Features</p>
            <p className={theme === "dark" ? "text-xl text-gray-300 max-w-3xl mx-auto" : "text-xl text-gray-600 max-w-3xl mx-auto"}>
              Experience the power of cutting-edge AI technology with our fast, reliable, and user-friendly image generation platform.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {features.map((feature, index) => (
              <div
                key={index}
                className={theme === "dark" ? "relative text-center p-10 rounded-3xl bg-black/60 border border-gray-700 shadow-2xl hover:scale-105 transition-transform duration-200 group overflow-hidden animate-on-scroll animate-scrollHidden" : "relative text-center p-10 rounded-3xl bg-white/60 backdrop-blur-xl shadow-2xl border border-white/40 hover:scale-105 transition-transform duration-200 group overflow-hidden animate-on-scroll animate-scrollHidden"}
                style={{ boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)', animationDelay: `${index * 0.1}s` }}
              >
                <div className={theme === "dark" ? "absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-gray-500 group-hover:shadow-[0_0_32px_0_rgba(75,85,99,0.25)] transition-all duration-300 pointer-events-none" : "absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-indigo-400 group-hover:shadow-[0_0_32px_0_rgba(99,102,241,0.25)] transition-all duration-300 pointer-events-none"} />
                <div className="relative z-10 flex flex-col items-center">
                  <div className={theme === "dark" ? "flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-gray-800 shadow-lg border-2 border-gray-700" : "flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-white shadow-lg border-2 border-indigo-100"}>
                    <span className="text-5xl drop-shadow-xl">{feature.icon}</span>
                  </div>
                  <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-200 mb-2 drop-shadow-sm" : "text-2xl font-bold text-gray-900 mb-2 drop-shadow-sm"}>{feature.title}</h3>
                  <p className={theme === "dark" ? "text-gray-300 text-base leading-relaxed" : "text-gray-700 text-base leading-relaxed"}>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive 3D Features Showcase */}
      <section className={theme === "dark" ? "py-20 bg-gray-900 relative overflow-hidden" : "py-20 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-hidden"}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll animate-scrollHidden">
            <h2 className={theme === "dark" ? "text-4xl font-extrabold text-gray-200 mb-4 drop-shadow" : "text-4xl font-extrabold text-gray-900 mb-4 drop-shadow"}>Three Powerful AI Tools in One</h2>
            <p className={theme === "dark" ? "text-xl text-gray-300 max-w-3xl mx-auto" : "text-xl text-gray-600 max-w-3xl mx-auto"}>
              Experience the full power of AI creativity with our comprehensive suite of tools
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Text to Image */}
            <div className={theme === "dark" ? "group perspective-1000 animate-on-scroll animate-scrollHidden" : "group perspective-1000 animate-on-scroll animate-scrollHidden"}>
              <div className={theme === "dark" ? "relative transform-style-3d transition-transform duration-700 group-hover:rotateY-12 group-hover:rotateX-5 group-hover:scale-105" : "relative transform-style-3d transition-transform duration-700 group-hover:rotateY-12 group-hover:rotateX-5 group-hover:scale-105"}>
                <div className={theme === "dark" ? "bg-gradient-to-br from-gray-800 via-gray-900 to-black border border-gray-700 rounded-3xl p-8 shadow-2xl backdrop-blur-xl relative z-10 hover:shadow-[0_25px_50px_-12px_rgba(75,85,99,0.25)] transition-all duration-500" : "bg-gradient-to-br from-white via-indigo-50 to-purple-50 border border-indigo-200 rounded-3xl p-8 shadow-2xl backdrop-blur-xl relative z-10 hover:shadow-[0_25px_50px_-12px_rgba(99,102,241,0.25)] transition-all duration-500"}>
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-400 via-purple-500 to-indigo-500 opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-500"></div>
                  <div className="text-center relative z-20">
                    <div className={theme === "dark" ? "w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-pink-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300" : "w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-pink-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300"}>
                      <span className="text-3xl text-white">✨</span>
                    </div>
                    <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-100 mb-4" : "text-2xl font-bold text-gray-900 mb-4"}>Text to Image</h3>
                    <p className={theme === "dark" ? "text-gray-300 mb-6" : "text-gray-600 mb-6"}>Transform your imagination into stunning visuals with simple text descriptions</p>
                    <div className={theme === "dark" ? "bg-gray-900/50 border border-gray-600 rounded-xl p-4 text-sm" : "bg-white/50 border border-indigo-200 rounded-xl p-4 text-sm"}>
                      <span className={theme === "dark" ? "text-gray-400 block mb-2" : "text-gray-500 block mb-2"}>Example:</span>
                      <span className={theme === "dark" ? "text-gray-200 italic" : "text-gray-700 italic"}>"A majestic dragon in a mystical forest"</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transform Image */}
            <div className={theme === "dark" ? "group perspective-1000 animate-on-scroll animate-scrollHidden" : "group perspective-1000 animate-on-scroll animate-scrollHidden"} style={{ animationDelay: '0.2s' }}>
              <div className={theme === "dark" ? "relative transform-style-3d transition-transform duration-700 group-hover:rotateY-12 group-hover:rotateX-5 group-hover:scale-105" : "relative transform-style-3d transition-transform duration-700 group-hover:rotateY-12 group-hover:rotateX-5 group-hover:scale-105"}>
                <div className={theme === "dark" ? "bg-gradient-to-br from-gray-800 via-gray-900 to-black border border-gray-700 rounded-3xl p-8 shadow-2xl backdrop-blur-xl relative z-10 hover:shadow-[0_25px_50px_-12px_rgba(75,85,99,0.25)] transition-all duration-500" : "bg-gradient-to-br from-white via-purple-50 to-pink-50 border border-purple-200 rounded-3xl p-8 shadow-2xl backdrop-blur-xl relative z-10 hover:shadow-[0_25px_50px_-12px_rgba(147,51,234,0.25)] transition-all duration-500"}>
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-500"></div>
                  <div className="text-center relative z-20">
                    <div className={theme === "dark" ? "w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300" : "w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300"}>
                      <span className="text-3xl text-white">🎨</span>
                    </div>
                    <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-100 mb-4" : "text-2xl font-bold text-gray-900 mb-4"}>Transform Image</h3>
                    <p className={theme === "dark" ? "text-gray-300 mb-6" : "text-gray-600 mb-6"}>Upload any image and transform it into different styles, art forms, or variations</p>
                    <div className={theme === "dark" ? "bg-gray-900/50 border border-gray-600 rounded-xl p-4 text-sm" : "bg-white/50 border border-purple-200 rounded-xl p-4 text-sm"}>
                      <span className={theme === "dark" ? "text-gray-400 block mb-2" : "text-gray-500 block mb-2"}>Example:</span>
                      <span className={theme === "dark" ? "text-gray-200 italic" : "text-gray-700 italic"}>Photo → Digital Art → Anime Style</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Image to Video */}
            <div className={theme === "dark" ? "group perspective-1000 animate-on-scroll animate-scrollHidden" : "group perspective-1000 animate-on-scroll animate-scrollHidden"} style={{ animationDelay: '0.4s' }}>
              <div className={theme === "dark" ? "relative transform-style-3d transition-transform duration-700 group-hover:rotateY-12 group-hover:rotateX-5 group-hover:scale-105" : "relative transform-style-3d transition-transform duration-700 group-hover:rotateY-12 group-hover:rotateX-5 group-hover:scale-105"}>
                <div className={theme === "dark" ? "bg-gradient-to-br from-gray-800 via-gray-900 to-black border border-gray-700 rounded-3xl p-8 shadow-2xl backdrop-blur-xl relative z-10 hover:shadow-[0_25px_50px_-12px_rgba(75,85,99,0.25)] transition-all duration-500" : "bg-gradient-to-br from-white via-pink-50 to-red-50 border border-pink-200 rounded-3xl p-8 shadow-2xl backdrop-blur-xl relative z-10 hover:shadow-[0_25px_50px_-12px_rgba(236,72,153,0.25)] transition-all duration-500"}>
                  <div className="absolute inset-0 bg-gradient-to-r from-red-400 via-pink-500 to-purple-500 opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-500"></div>
                  <div className="text-center relative z-20">
                    <div className={theme === "dark" ? "w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300" : "w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300"}>
                      <span className="text-3xl text-white">🎬</span>
                    </div>
                    <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-100 mb-4" : "text-2xl font-bold text-gray-900 mb-4"}>Image to Video</h3>
                    <p className={theme === "dark" ? "text-gray-300 mb-6" : "text-gray-600 mb-6"}>Bring your images to life by converting them into captivating animated videos</p>
                    <div className={theme === "dark" ? "bg-gray-900/50 border border-gray-600 rounded-xl p-4 text-sm" : "bg-white/50 border border-pink-200 rounded-xl p-4 text-sm"}>
                      <span className={theme === "dark" ? "text-gray-400 block mb-2" : "text-gray-500 block mb-2"}>Example:</span>
                      <span className={theme === "dark" ? "text-gray-200 italic" : "text-gray-700 italic"}>Static Portrait → Animated Character</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section className={theme === "dark" ? "py-20 bg-black animate-on-scroll animate-scrollHidden" : "py-20 bg-gradient-to-br from-white via-indigo-50 to-pink-50 animate-on-scroll animate-scrollHidden"}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll animate-scrollHidden">
            <h2 className={theme === "dark" ? "text-4xl font-bold text-gray-200 mb-4" : "text-4xl font-bold text-gray-900 mb-4"}>See What You Can Create</h2>
            <p className={theme === "dark" ? "text-xl text-gray-300 max-w-3xl mx-auto" : "text-xl text-gray-600 max-w-3xl mx-auto"}>
              From text to image, image transformations to animated videos - explore the limitless possibilities
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {examples.map((example) => (
              <div key={example.id} className={theme === "dark" ? "group perspective-1000 bg-black border border-gray-700 rounded-2xl shadow-xl overflow-hidden transition-all duration-500 animate-on-scroll animate-scrollHidden hover:scale-105 hover:rotateY-8 hover:shadow-[0_20px_50px_-12px_rgba(75,85,99,0.3)]" : "group perspective-1000 bg-white/90 rounded-2xl shadow-xl overflow-hidden transition-all duration-500 animate-on-scroll animate-scrollHidden hover:scale-105 hover:rotateY-8 hover:shadow-[0_20px_50px_-12px_rgba(99,102,241,0.3)]"} style={{ animationDelay: `${example.id * 0.1}s` }}>
                <div className="relative transform-style-3d transition-transform duration-700">
                  <div className="relative">
                    {example.isVideo && example.videoUrl ? (
                      <video
                        className={`w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110 ${theme === "dark" ? "bg-gray-900" : "bg-gradient-to-br from-indigo-100 to-pink-100"}`}
                        autoPlay
                        loop
                        muted
                        playsInline
                        onError={(e) => {
                          console.error("Video failed to load, falling back to image");
                          e.target.style.display = 'none';
                          const img = e.target.nextElementSibling;
                          if (img) img.style.display = 'block';
                        }}
                      >
                        <source src={example.videoUrl} type="video/mp4" />
                      </video>
                    ) : null}
                    <img
                      src={example.image}
                      alt={example.prompt}
                      className={`w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110 ${theme === "dark" ? "bg-gray-900" : "bg-gradient-to-br from-indigo-100 to-pink-100"} ${example.isVideo && example.videoUrl ? 'hidden' : ''}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/placeholder.jpg";
                      }}
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium shadow transform transition-transform duration-300 group-hover:scale-110 ${
                        example.type === 'text-to-image' 
                          ? (theme === "dark" ? "bg-gradient-to-r from-pink-500 to-indigo-500 text-white" : "bg-gradient-to-r from-pink-500 to-indigo-500 text-white")
                          : example.type === 'transform-image'
                          ? (theme === "dark" ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" : "bg-gradient-to-r from-purple-500 to-pink-500 text-white")
                          : (theme === "dark" ? "bg-gradient-to-r from-red-500 to-pink-500 text-white" : "bg-gradient-to-r from-red-500 to-pink-500 text-white")
                      }`}>
                        {example.category}
                      </span>
                    </div>
                    {example.isVideo && (
                      <div className="absolute top-4 right-4">
                        <div className={theme === "dark" ? "bg-black/80 text-white p-2 rounded-full shadow-lg transform transition-transform duration-300 group-hover:scale-110" : "bg-white/80 text-gray-900 p-2 rounded-full shadow-lg transform transition-transform duration-300 group-hover:scale-110"}>
                          <span className="text-lg">🎬</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <p className={theme === "dark" ? "text-gray-300 text-sm leading-relaxed" : "text-gray-700 text-sm leading-relaxed"}>{example.prompt}</p>
                    {example.isVideo && (
                      <div className="mt-3 flex items-center space-x-2">
                        <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse"></div>
                        <span className={theme === "dark" ? "text-xs text-gray-400" : "text-xs text-gray-500"}>Live Video Preview</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className={theme === "dark" ? "py-20 bg-gray-900 animate-on-scroll animate-scrollHidden" : "py-20 bg-white animate-on-scroll animate-scrollHidden"}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll animate-scrollHidden">
            <h2 className={theme === "dark" ? "text-4xl font-bold text-gray-200 mb-4" : "text-4xl font-bold text-gray-900 mb-4"}>How It Works</h2>
            <p className={theme === "dark" ? "text-xl text-gray-300 max-w-3xl mx-auto" : "text-xl text-gray-600 max-w-3xl mx-auto"}>
              Creating amazing images is as simple as describing what you want to see.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center animate-on-scroll animate-scrollHidden" style={{ animationDelay: '0.1s' }}>
              <div className={theme === "dark" ? "w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-700" : "w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4"}>
                <span className={theme === "dark" ? "text-2xl font-bold text-gray-200" : "text-2xl font-bold text-indigo-600"}>1</span>
              </div>
              <h3 className={theme === "dark" ? "text-xl font-semibold text-gray-200 mb-2" : "text-xl font-semibold text-gray-900 mb-2"}>Describe Your Vision</h3>
              <p className={theme === "dark" ? "text-gray-300" : "text-gray-600"}>Write a detailed description of the image you want to create</p>
            </div>
            <div className="text-center animate-on-scroll animate-scrollHidden" style={{ animationDelay: '0.2s' }}>
              <div className={theme === "dark" ? "w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-700" : "w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4"}>
                <span className={theme === "dark" ? "text-2xl font-bold text-gray-200" : "text-2xl font-bold text-indigo-600"}>2</span>
              </div>
              <h3 className={theme === "dark" ? "text-xl font-semibold text-gray-200 mb-2" : "text-xl font-semibold text-gray-900 mb-2"}>AI Generates</h3>
              <p className={theme === "dark" ? "text-gray-300" : "text-gray-600"}>Our advanced AI model creates your image in seconds</p>
            </div>
            <div className="text-center animate-on-scroll animate-scrollHidden" style={{ animationDelay: '0.3s' }}>
              <div className={theme === "dark" ? "w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-700" : "w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4"}>
                <span className={theme === "dark" ? "text-2xl font-bold text-gray-200" : "text-2xl font-bold text-indigo-600"}>3</span>
              </div>
              <h3 className={theme === "dark" ? "text-xl font-semibold text-gray-200 mb-2" : "text-xl font-semibold text-gray-900 mb-2"}>Download & Use</h3>
              <p className={theme === "dark" ? "text-gray-300" : "text-gray-600"}>Download your high-quality image and use it anywhere</p>
            </div>
          </div>
        </div>
      </section>

      {/* Prompt Examples Section */}
      <section className={theme === "dark" ? "py-20 bg-black animate-on-scroll animate-scrollHidden" : "py-20 bg-gray-50 animate-on-scroll animate-scrollHidden"}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={theme === "dark" ? "text-4xl font-bold text-gray-200 mb-4" : "text-4xl font-bold text-gray-900 mb-4"}>Get Inspired</h2>
            <p className={theme === "dark" ? "text-xl text-gray-300 max-w-3xl mx-auto" : "text-xl text-gray-600 max-w-3xl mx-auto"}>
              Try these example prompts to get started with your creative journey.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {promptExamples.map((prompt, index) => (
              <div key={index} className={theme === "dark" ? "bg-black border border-gray-700 p-4 rounded-lg hover:border-gray-600 transition-colors animate-popIn" : "bg-white p-4 rounded-lg border hover:border-indigo-300 transition-colors animate-popIn"} style={{ animationDelay: `${index * 0.1}s` }}>
                <p className={theme === "dark" ? "text-gray-300 text-sm" : "text-gray-700 text-sm"}>"{prompt}"</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/register"
              className={theme === "dark" ? "bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-700 transition-colors border border-gray-700" : "bg-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-indigo-700 transition-colors"}
            >
              Start Creating Now
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section ref={pricingRef} id="pricing" className={theme === "dark" ? "py-20 bg-gray-900 animate-on-scroll animate-scrollHidden" : "py-20 bg-white animate-on-scroll animate-scrollHidden"}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={theme === "dark" ? "text-4xl font-bold text-gray-200 mb-4" : "text-4xl font-bold text-gray-900 mb-4"}>Simple, Transparent Pricing</h2>
            <p className={theme === "dark" ? "text-xl text-gray-300 max-w-3xl mx-auto" : "text-xl text-gray-600 max-w-3xl mx-auto"}>
              Pay only for what you use with our credit-based system. No hidden fees, no surprises.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className={theme === "dark" ? "bg-gray-900 border border-gray-700 p-8 rounded-lg text-center animate-popIn" : "bg-gray-50 p-8 rounded-lg text-center animate-popIn"} style={{ animationDelay: '0.1s' }}>
              <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-200 mb-2" : "text-2xl font-bold text-gray-900 mb-2"}>Basic Credits</h3>
              <p className={theme === "dark" ? "text-gray-400 mb-4" : "text-gray-500 mb-4"}>Perfect for occasional use</p>
              <div className={theme === "dark" ? "text-4xl font-bold text-gray-200 mb-4" : "text-4xl font-bold text-indigo-600 mb-4"}>₹10</div>
              <p className={theme === "dark" ? "text-gray-300 mb-6" : "text-gray-600 mb-6"}>100 credits • 10 images</p>
              <ul className="text-left space-y-2 mb-6">
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>High quality images</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>No watermarks</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>Credits never expire</span>
                </li>
                <li className="flex items-center">
                  <span className={theme === "dark" ? "text-gray-400 mr-2" : "text-gray-500 mr-2"}>•</span>
                  <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>Uses default settings</span>
                </li>
              </ul>
              <Link
                to="/register"
                className={theme === "dark" ? "block w-full bg-gray-800 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors border border-gray-700" : "block w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"}
              >
                Buy Credits
              </Link>
            </div>
            <div className={theme === "dark" ? "bg-gray-800 border border-gray-600 p-8 rounded-lg text-center animate-popIn" : "bg-indigo-50 p-8 rounded-lg text-center border-2 border-indigo-200 animate-popIn"} style={{ animationDelay: '0.2s' }}>
              <div className={theme === "dark" ? "bg-gray-800 text-white px-4 py-1 rounded-full text-sm font-medium inline-block mb-4 border border-gray-700" : "bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-medium inline-block mb-4"}>
                Most Popular
              </div>
              <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-200 mb-2" : "text-2xl font-bold text-gray-900 mb-2"}>Pro Credits</h3>
              <p className={theme === "dark" ? "text-gray-400 mb-4" : "text-gray-500 mb-4"}>Best for power users and professionals</p>
              <div className={theme === "dark" ? "text-4xl font-bold text-gray-200 mb-4" : "text-4xl font-bold text-indigo-600 mb-4"}>₹299</div>
              <p className={theme === "dark" ? "text-gray-300 mb-6" : "text-gray-600 mb-6"}>5000 credits • 500 images</p>
              <ul className="text-left space-y-2 mb-6">
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>Highest quality images</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>Priority support</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>No watermarks</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>Credits never expire</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>Advanced image controls</span>
                </li>
              </ul>
              <Link
                to="/register"
                className={theme === "dark" ? "block w-full bg-gray-800 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors border border-gray-700" : "block w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"}
              >
                Buy Pro Credits
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={theme === "dark" ? "py-20 bg-black text-white animate-on-scroll animate-scrollHidden" : "py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white animate-on-scroll animate-scrollHidden"}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 animate-popIn">
          <h2 className="text-4xl font-bold mb-4">Ready to Create Something Amazing?</h2>
          <p className={theme === "dark" ? "text-xl mb-8 text-gray-300" : "text-xl mb-8 text-indigo-100"}>
            Join thousands of creators who are already using our AI to bring their ideas to life.
          </p>
          <Link
            to="/register"
            className={theme === "dark" ? "bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-700 transition-colors shadow-lg animate-glow border border-gray-700" : "bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg animate-glow"}
          >
            Start Creating Free
          </Link>
        </div>
      </section>

      {/* Global Animation Styles */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes gradientText { 0% { background-position: 0% 50%; } 100% { background-position: 100% 50%; } }
        @keyframes zoomIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes heavyScrollIn { from { opacity: 0; transform: translateY(120px) scale(0.8) rotateX(15deg); } to { opacity: 1; transform: translateY(0) scale(1) rotateX(0deg); } }
        @keyframes heavyScrollOut { from { opacity: 1; transform: translateY(0) scale(1) rotateX(0deg); } to { opacity: 0; transform: translateY(-60px) scale(0.9) rotateX(-8deg); } }
        @keyframes slideInLeft { from { opacity: 0; transform: translateX(-100px) scale(0.9); } to { opacity: 1; transform: translateX(0) scale(1); } }
        @keyframes slideInRight { from { opacity: 0; transform: translateX(100px) scale(0.9); } to { opacity: 1; transform: translateX(0) scale(1); } }
        @keyframes slideInUp { from { opacity: 0; transform: translateY(80px) scale(0.9); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.5); } to { opacity: 1; transform: scale(1); } }
        @keyframes rotateIn { from { opacity: 0; transform: rotate(-180deg) scale(0.5); } to { opacity: 1; transform: rotate(0deg) scale(1); } }
        @keyframes bounceInUp { from { opacity: 0; transform: translateY(100px) scale(0.7); } 60% { opacity: 1; transform: translateY(-20px) scale(1.1); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.8s cubic-bezier(.4,0,.2,1) both; }
        .animate-fadeInUp { animation: fadeInUp 0.6s cubic-bezier(.4,0,.2,1) both; }
        .animate-gradientText { animation: gradientText 2s ease-in-out infinite alternate; background-size: 200% 200%; }
        .animate-zoomIn { animation: zoomIn 0.5s cubic-bezier(.4,0,.2,1) both; }
        .animate-scrollHidden { opacity: 0; transform: translateY(120px) scale(0.8) rotateX(15deg); transition: all 0.5s cubic-bezier(.4,0,.2,1); }
        .animate-scrollVisible { opacity: 1; transform: translateY(0) scale(1) rotateX(0deg); transition: all 0.5s cubic-bezier(.4,0,.2,1); }
        .animate-on-scroll { transition: all 0.5s cubic-bezier(.4,0,.2,1); }
        .animate-heavyFadeInUp { animation: heavyFadeInUp 0.8s cubic-bezier(.4,0,.2,1) both; }
        .animate-popIn { animation: popIn 0.6s cubic-bezier(.4,0,.2,1) both; }
        .animate-bounceIn { animation: bounceIn 0.7s cubic-bezier(.4,0,.2,1) both; }
        .animate-galleryEntrance { animation: galleryEntrance 0.8s cubic-bezier(.4,0,.2,1) both; }
        .animate-heavyFloat { animation: heavyFloat 4s ease-in-out infinite; }
        .animate-heavyFloatReverse { animation: heavyFloatReverse 4s ease-in-out infinite; }
        .animate-heavyScale { animation: heavyScale 3s ease-in-out infinite; }
        .animate-heavyRotate { animation: heavyRotate 6s linear infinite; }
        .animate-logoSpin { animation: logoSpin 1.5s cubic-bezier(.4,0,.2,1) both; }
        .animate-slideInLeft { animation: slideInLeft 0.5s cubic-bezier(.4,0,.2,1) both; }
        .animate-slideInRight { animation: slideInRight 0.5s cubic-bezier(.4,0,.2,1) both; }
        .animate-slideInUp { animation: slideInUp 0.5s cubic-bezier(.4,0,.2,1) both; }
        .animate-scaleIn { animation: scaleIn 0.5s cubic-bezier(.4,0,.2,1) both; }
        .animate-rotateIn { animation: rotateIn 0.5s cubic-bezier(.4,0,.2,1) both; }
        .animate-bounceInUp { animation: bounceInUp 0.5s cubic-bezier(.4,0,.2,1) both; }
        
        /* 3D Transform utilities */
        .perspective-1000 { perspective: 1000px; }
        .transform-style-3d { transform-style: preserve-3d; }
        .rotateY-12 { transform: rotateY(12deg); }
        .rotateX-5 { transform: rotateX(5deg); }
        .group:hover .group-hover\:rotateY-12 { transform: rotateY(12deg) rotateX(5deg) scale(1.05); }
        .group:hover .group-hover\:rotateX-5 { transform: rotateX(5deg); }
        .group:hover .group-hover\:scale-105 { transform: scale(1.05); }
        .hover\:rotateY-8:hover { transform: rotateY(8deg) scale(1.05); }
        
        /* Hide scrollbar */
        .overflow-x-auto::-webkit-scrollbar {
          display: none;
        }
        
        /* Smooth auto-scroll */
        .scroll-smooth {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  )
}

export default Home
